import * as React from 'react';
import { flexRender, type Table as TanStackTable, type Row } from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { useIsMobileTable } from '@/hooks/useMobile';
import { DataTableCards, type CardField } from './DataTableCards';

const MIN_COLUMN_WIDTH = 100;
const FIXED_COLUMN_IDS = new Set(['select', 'expand']);

// ============================================================================
// DataTable
// ============================================================================

interface DataTableProps<TData> {
  table: TanStackTable<TData>;
  isLoading?: boolean;
  isFetching?: boolean;
  stickyHeader?: boolean;
  renderSubRow?: (row: Row<TData>) => React.ReactNode;
  onRowClick?: (row: Row<TData>) => void;
  mobileCardFields?: CardField<TData>[];
  mobileGetImageUrl?: (row: Row<TData>) => string | undefined;
}

export function DataTable<TData>({
  table,
  isLoading,
  isFetching,
  stickyHeader = true,
  renderSubRow,
  onRowClick,
  mobileCardFields,
  mobileGetImageUrl,
}: DataTableProps<TData>): React.JSX.Element {
  const isMobile = useIsMobileTable();
  const columns = table.getAllColumns();

  if (isMobile && mobileCardFields && mobileCardFields.length > 0) {
    return (
      <DataTableCards
        table={table}
        isLoading={isLoading}
        isFetching={isFetching}
        cardFields={mobileCardFields}
        getImageUrl={mobileGetImageUrl}
        onRowClick={onRowClick}
      />
    );
  }

  const cellPaddingClass = 'pt-1 pb-2 px-4';
  const getCellPadding = (columnSize: number) =>
    columnSize <= 50 ? 'pt-1 pb-2 px-2' : cellPaddingClass;
  const getCellPaddingForColumn = (columnSize: number, columnId: string) => {
    const base = getCellPadding(columnSize);
    if (columnId === 'select') return 'pt-1 pb-2 pl-2 pr-0';
    if (columnId === 'expand') return 'pt-2 pb-2 pl-2 pr-0';
    return base;
  };

  if (isLoading) {
    return (
      <div className='rounded-md border'>
        <Table>
          <TableHeader className={stickyHeader ? 'sticky top-0 z-10 bg-background' : ''}>
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.id}
                  className={getCellPaddingForColumn(column.getSize(), column.id)}
                >
                  <Skeleton className='h-4 w-24' />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 10 }).map((_, index) => (
              <TableRow key={index}>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={getCellPaddingForColumn(column.getSize(), column.id)}
                  >
                    <Skeleton className='h-4 w-full' />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  const isRefetching = isFetching && !isLoading;

  return (
    <div className='relative overflow-x-auto rounded-md border'>
      {isRefetching && (
        <div className='absolute inset-0 z-20 flex items-center justify-center bg-background/50 rounded-md'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      )}
      <Table style={{ tableLayout: 'fixed', minWidth: '100%' }}>
        <TableHeader className={cn(stickyHeader && 'sticky top-0 z-10 bg-background shadow-sm')}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const isFixed = FIXED_COLUMN_IDS.has(header.column.id);
                const minWidth = isFixed
                  ? header.getSize()
                  : Math.max(header.getSize(), MIN_COLUMN_WIDTH);
                return (
                  <TableHead
                    key={header.id}
                    className={cn(
                      getCellPaddingForColumn(header.getSize(), header.column.id),
                      'whitespace-nowrap relative',
                      isFixed ? 'overflow-visible' : 'overflow-hidden',
                    )}
                    style={{
                      width: header.getSize(),
                      minWidth,
                      maxWidth: header.column.getCanResize() ? undefined : header.getSize(),
                    }}
                  >
                    <div className='min-w-0 overflow-visible pr-1'>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                    {header.column.getCanResize() && (
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={cn(
                          'absolute right-0 top-0 h-full w-3 cursor-col-resize select-none touch-none',
                          'flex items-center justify-end',
                          'after:absolute after:right-1 after:h-full after:w-px after:bg-border',
                          'hover:after:bg-primary/60',
                          header.column.getIsResizing() && 'after:bg-primary',
                        )}
                        style={{ touchAction: 'none' }}
                        aria-label='Resize column'
                      />
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  className={cn(
                    row.getIsExpanded() && 'bg-muted/30',
                    row.getIsSelected() && 'bg-muted/50',
                    onRowClick && 'cursor-pointer hover:bg-muted/50 transition-colors',
                  )}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    const isInteractive = target.closest(
                      'button, a, input, select, textarea, [role="button"], [role="checkbox"], [role="combobox"]',
                    );
                    if (!isInteractive && onRowClick) {
                      onRowClick(row);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isFixed = FIXED_COLUMN_IDS.has(cell.column.id);
                    const minWidth = isFixed
                      ? cell.column.getSize()
                      : Math.max(cell.column.getSize(), MIN_COLUMN_WIDTH);
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          getCellPaddingForColumn(cell.column.getSize(), cell.column.id),
                          isFixed ? 'overflow-visible' : 'overflow-hidden',
                        )}
                        style={{
                          width: cell.column.getSize(),
                          minWidth,
                          maxWidth: cell.column.getCanResize() ? undefined : cell.column.getSize(),
                        }}
                      >
                        <div className='min-w-0 overflow-visible'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
                {row.getIsExpanded() && renderSubRow && (
                  <TableRow className='bg-muted/20 hover:bg-muted/30'>
                    <TableCell colSpan={row.getVisibleCells().length} className='p-0'>
                      {renderSubRow(row as Row<TData>)}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-24 text-center'>
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================================================
// DataTablePagination
// ============================================================================

interface DataTablePaginationProps<TData> {
  table: TanStackTable<TData>;
  pageSizeOptions?: number[];
  showSelectedCount?: boolean;
}

export function DataTablePagination<TData>({
  table,
  pageSizeOptions = [10, 25, 50, 100],
  showSelectedCount = true,
}: DataTablePaginationProps<TData>): React.JSX.Element {
  const totalRows = table.getFilteredRowModel().rows.length;

  return (
    <div className='flex flex-col gap-3 px-2 py-4 sm:flex-row sm:items-center sm:justify-between'>
      <div className='text-sm text-muted-foreground text-center sm:text-left'>
        {showSelectedCount && table.getFilteredSelectedRowModel().rows.length > 0 ? (
          <span>
            {table.getFilteredSelectedRowModel().rows.length} of {totalRows} row(s) selected.
          </span>
        ) : (
          <span>{totalRows} total rows</span>
        )}
      </div>

      <div className='flex flex-wrap items-center justify-center gap-4 sm:justify-end sm:gap-6 lg:gap-8'>
        <div className='hidden items-center space-x-2 sm:flex'>
          <p className='text-sm font-medium whitespace-nowrap'>Rows per page</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className='h-8 w-[70px]'>
              <SelectValue placeholder={String(table.getState().pagination.pageSize)} />
            </SelectTrigger>
            <SelectContent side='top'>
              {pageSizeOptions.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className='flex items-center justify-center text-sm font-medium whitespace-nowrap'>
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() || 1}
        </div>

        <div className='flex items-center space-x-1 sm:space-x-2'>
          <Button
            variant='outline'
            className='hidden h-8 w-8 p-0 lg:flex'
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            type='button'
          >
            <span className='sr-only'>Go to first page</span>
            <ChevronsLeft className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            className='h-8 w-8 p-0'
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            type='button'
          >
            <span className='sr-only'>Go to previous page</span>
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            className='h-8 w-8 p-0'
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            type='button'
          >
            <span className='sr-only'>Go to next page</span>
            <ChevronRight className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            className='hidden h-8 w-8 p-0 lg:flex'
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            type='button'
          >
            <span className='sr-only'>Go to last page</span>
            <ChevronsRight className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DataTableColumnHeader
// ============================================================================

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: import('@tanstack/react-table').Column<TData, TValue>;
  title: string;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: DataTableColumnHeaderProps<TData, TValue>): React.JSX.Element {
  if (!column.getCanSort()) {
    return <div className={className}>{title}</div>;
  }

  const isSorted = column.getIsSorted();

  return (
    <Button
      variant='ghost'
      size='sm'
      className={cn(
        '-ml-3 h-8 text-sm data-[state=open]:bg-accent',
        isSorted && 'text-primary hover:text-primary',
      )}
      onClick={() => column.toggleSorting(isSorted === 'asc')}
      type='button'
    >
      <span>{title}</span>
      {isSorted === 'desc' ? (
        <ArrowDown className='ml-2 h-4 w-4' />
      ) : isSorted === 'asc' ? (
        <ArrowUp className='ml-2 h-4 w-4' />
      ) : (
        <ArrowUpDown className='ml-2 h-4 w-4' />
      )}
    </Button>
  );
}
